import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ConversationalForm.css';
import { db } from '../firebaseConfig';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, where } from 'firebase/firestore';

const questionsData = [
  { id: 'sede', text: '¡Hola! ¿En cuál de nuestras sedes te gustaría reservar?', type: 'select', options: [] },
  { id: 'people', text: '¡Perfecto! ¿Para cuántas personas?', type: 'number' },
  { id: 'date', text: 'Entendido. ¿Qué día te gustaría venir?', type: 'date' },
  { id: 'time', text: '¿A qué hora?', type: 'time' },
  { id: 'name', text: 'Genial. ¿A nombre de quién hacemos la reserva?', type: 'text' },
  { id: 'email', text: '¿Y cuál es tu correo electrónico para enviarte la confirmación?', type: 'email' },
  { id: 'phone', text: 'Por favor, ingresa tu número de teléfono:', type: 'tel' }, // New question
  { id: 'terms', text: 'Antes de confirmar, por favor, lee y acepta nuestros términos y condiciones:', type: 'terms' },
  { id: 'summary', text: '¡Casi listo! Por favor, confirma que los datos son correctos:', type: 'summary' },
];

function ConversationalForm({ bookingData, onDataChange, currentStep, onStepChange, initialSede, onReservationSuccess }) {
  const [questions, setQuestions] = useState(questionsData);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [allSedes, setAllSedes] = useState([]); // New state to store all sedes
  const [errorMessage, setErrorMessage] = useState(''); // New state for error messages
  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Helper function to check if a time is within a schedule
  const isTimeWithinSchedule = (selectedDateStr, selectedTimeStr, sedeSchedule) => {
    if (!sedeSchedule) return false;

    const date = new Date(selectedDateStr);
    const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const dayOfWeek = days[date.getDay()];

    const daySchedule = sedeSchedule[dayOfWeek];
    if (!daySchedule || daySchedule.length === 0) {
      return false; // No schedule for this day
    }

    for (const slot of daySchedule) {
      if (selectedTimeStr >= slot.start && selectedTimeStr <= slot.end) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const getSedes = async () => {
      const sedesCollection = collection(db, 'reservasSedes');
      const q = query(sedesCollection, orderBy('createdAt', 'asc'));
      const sedesSnapshot = await getDocs(q);
      const sedesData = sedesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllSedes(sedesData); // Store all sedes in state

      const activeSedes = sedesData.filter(sede => sede.isActive);
      const sedesList = activeSedes.map(sede => sede.title); // Use sede.title for reservasSedes
      
      const updatedQuestions = [...questions];
      updatedQuestions.find(q => q.id === 'sede').options = sedesList;
      setQuestions(updatedQuestions);

      if (initialSede) {
        const sedeTitle = sedesList.find(s => s.replace(/\s+/g, '-').toLowerCase() === initialSede);
        if (sedeTitle) {
          const selectedSedeObject = sedesData.find(s => s.title === sedeTitle); // Find the full sede object by title
          onDataChange({ 
            ...bookingData,
            sede: sedeTitle,
            selectedSedeSchedule: selectedSedeObject ? selectedSedeObject.schedule : {}
          });
          setChatHistory([
            { type: 'gemini', text: questions[0].text },
            { type: 'user', text: sedeTitle },
          ]);
          onStepChange(1);
        }
      }
    };
    getSedes();
  }, [initialSede]);

  const handleNextQuestion = () => {
    const currentQuestion = questions[currentStep];
    setErrorMessage(''); // Clear previous error message

    if (currentQuestion.type === 'terms') {
      if (termsAccepted) {
        onDataChange({ [currentQuestion.id]: 'Aceptados' });
        setChatHistory([
          ...chatHistory,
          { type: 'gemini', text: currentQuestion.text },
          { type: 'user', text: 'He leído y acepto los términos y condiciones.' },
        ]);
        onStepChange(currentStep + 1);
      }
      return;
    }

    if (!currentInput) {
      setErrorMessage('Por favor, ingresa una respuesta.');
      return;
    }

    // Time validation logic
    if (currentQuestion.id === 'time') {
      const selectedDate = bookingData.date;
      const selectedSedeSchedule = bookingData.selectedSedeSchedule;

      if (!selectedDate || !selectedSedeSchedule) {
        setErrorMessage('Falta información de la fecha o el horario de la sede para validar la hora.');
        return;
      }

      if (!isTimeWithinSchedule(selectedDate, currentInput, selectedSedeSchedule)) {
        setErrorMessage('La hora seleccionada está fuera del horario de atención de la sede para la fecha indicada. Por favor, elige otra hora.');
        return;
      }
    }

    // If the current question is 'sede', find the selected sede object and store its schedule
    if (currentQuestion.id === 'sede') {
      const selectedSedeObject = allSedes.find(s => s.title === currentInput); // Use allSedes state and sede.title
      onDataChange({ 
        [currentQuestion.id]: currentInput,
        selectedSedeSchedule: selectedSedeObject ? selectedSedeObject.schedule : {}
      });
    } else {
      onDataChange({ [currentQuestion.id]: currentInput });
    }
    
    setChatHistory([
      ...chatHistory,
      { type: 'gemini', text: currentQuestion.text },
      { type: 'user', text: currentInput },
    ]);
    setCurrentInput('');
    onStepChange(currentStep + 1);
  };

  const handleConfirmReservation = async () => {
    try {
      await addDoc(collection(db, 'reservations'), {
        ...bookingData,
        guests: bookingData.people, // Explicitly map 'people' to 'guests'
        createdAt: serverTimestamp(),
      });
      alert('¡Reserva Confirmada!');
      if (onReservationSuccess) {
        onReservationSuccess();
      }
      navigate('/'); // Redirect to home page
      // Reset logic should be handled by parent now
    } catch (error) {
      console.error('Error al guardar la reserva:', error);
      alert('Hubo un error al confirmar tu reserva. Por favor, inténtalo de nuevo.');
    }
  };

  const renderCurrentQuestion = () => {
    if (currentStep >= questions.length) return null;

    const question = questions[currentStep];

    if (question.type === 'terms') {
      return (
        <div className="terms-card">
          <div className="gemini-message"><p>{question.text}</p></div>
          <div className="terms-content">
            <p>Entiendo y acepto los siguientes terminos y condiciones:</p>
            <ul>
              <li>Se puntual, ya que solo garantizamos la reserva por 15 minutos después de la hora acordada.</li>
              <li>Cualquier cancelación debe realizarse con al menos 2 horas de anticipación.</li>
              <li>Las reservas para más de 4 personas requieren llegar juntos.</li>
              <li>Nos reservamos el derecho de admisión.</li>
              <li>No se permite el ingreso de alimentos y/o bebidas, personas en estado de embriaguez o bajo el efecto de sustancias alucinogenas.</li>
              <li>Luego de 15 minutos sin consumir, el personal podrá disponer de la mesa.</li>
            </ul>
            <label className="terms-checkbox">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              Acepto los términos y condiciones
            </label>
          </div>
          <button
            onClick={handleNextQuestion}
            className="next-button"
            disabled={!termsAccepted}
          >
            Siguiente
          </button>
        </div>
      );
    }

    if (question.type === 'summary') {
      // This part is now in BookingSummary.js, but we need a confirmation button here.
      return (
          <button onClick={handleConfirmReservation} className="submit-button">Confirmar Reserva</button>
      );
    }

    return (
      <div className="current-question">
        <div className="gemini-message"><p>{question.text}</p></div>
        {errorMessage && <p className="error-message">{errorMessage}</p>} {/* Display error message */}
        <div className="input-area">
          {question.type === 'select' ? (
            <select value={currentInput} onChange={(e) => setCurrentInput(e.target.value)} autoFocus>
              <option value="">Selecciona una opción...</option>
              {question.options.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          ) : (
            <input
              type={question.type}
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              autoFocus
            />
          )}
          <button onClick={handleNextQuestion} className="next-button">Siguiente</button>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-window">
        <div className="chat-history">
          {chatHistory.map((msg, index) => (
            <div key={index} className={`${msg.type}-message`}>
              <p>{msg.text}</p>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        {renderCurrentQuestion()}
      </div>
  );
}

export default ConversationalForm;